/**
 * @module CanvasAttributeChart
 * @purpose Horizontal stacked bar showing artefact type distribution from live facets.
 * @depends FacetEntry (types/analysis-artefacts)
 * @dependedOnBy CanvasResultsArea
 *
 * @rules
 * - Performance: pure display from props
 * - State: none
 * - Errors: renders empty bar when attributeData is empty
 */

import type { FacetEntry } from '../../../types/analysis-artefacts'

const PERCENT_MULTIPLIER = 100
const SEGMENT_COLOURS = [
  '#6b7fa3',
  '#7a8f6e',
  '#8a7a6b',
  '#7a6b8a',
  '#6b8a8a',
  '#8a8a6b',
  '#8a6b6b',
]

interface CanvasAttributeChartProps {
  attributeData: FacetEntry[]
}

/**
 * Stacked horizontal bar of artefact type counts.
 *
 * @behaviour Width of each segment is proportional to its count vs total.
 *            Renders nothing visible when attributeData is empty.
 * @errors n/a
 */
export function CanvasAttributeChart({ attributeData }: CanvasAttributeChartProps): React.ReactElement {
  const total = attributeData.reduce((sum, entry) => sum + entry.count, 0)

  return (
    <div
      className="flex h-6 w-full overflow-hidden rounded"
      role="img"
      aria-label="Artefact type distribution"
    >
      {attributeData.map((entry, index) => {
        const widthPercent = total > 0 ? (entry.count / total) * PERCENT_MULTIPLIER : 0
        const colour = SEGMENT_COLOURS[index % SEGMENT_COLOURS.length] ?? '#6b7fa3'
        return (
          <div
            key={entry.id}
            title={`${entry.label}: ${String(entry.count)}`}
            style={{ width: `${String(widthPercent)}%`, background: colour }}
          />
        )
      })}
    </div>
  )
}
